import {
  FluidAlignment,
  FluidColor,
  FluidLanguage,
  FluidTextOverflow,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { TranslatableSlot } from '../../common/fluid-component-rendering.components';
import {
  setI18nState,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { propSync } from '../../utils/logging/deprecation.utils';
import { Utils } from '../../utils/utils';

/**
 * @displayName Text
 *
 * @slot - Text displayed within the component.
 */
@Component({
  tag: 'fluid-text',
  styleUrl: 'fluid-text.less',
  shadow: true,
})
export class FluidText implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidTextElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: { content?: string };
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;
  @State() _lang: FluidLanguage;
  @State() _i18nBound: boolean;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * When true, FLUID Text will render an embedded <strong> tag with bold
   * font weight that is displayed as an "inline" element (instead of the
   * default <p> tag that is displayed as a "block" element). NOTE: this
   * Prop denotes that FLUID Text's content has strong importance,
   * seriousness, or urgency.
   */
  @Prop({ mutable: true }) strong: boolean;

  /**
   * When true, FLUID Text will be displayed as an "inline-block" element
   * (instead of a "block" element) with right padding (for spacing
   * between consecutive inline elements).
   */
  @Prop({ mutable: true }) inline: boolean;

  /**
   * When true, applies "super" vertical alignment (i.e. superscript
   * format) to the text.
   */
  @Prop({ mutable: true }) alignSuper: boolean;

  /**
   * When true, FLUID Text will be displayed as a "block" element. NOTE:
   * this Prop is only applicable when "strong" Prop is true (default
   * FLUID Text renders an embedded <p> tag, which is already a "block"
   * element).
   */
  @Prop({ mutable: true }) block: boolean;

  /**
   * When true, text will be italicized.
   */
  @Prop({ mutable: true }) italics: boolean;

  /**
   * When true, text will be center-aligned.
   */
  @Prop({ mutable: true }) center: boolean;

  /**
   * When true, text will be slightly smaller & will have a lighter color.
   */
  @Prop({ mutable: true }) muted: boolean;

  /**
   * Removes default top & bottom margins when true.
   */
  @Prop({ mutable: true }) disableGutters: boolean;

  /**
   * When true, removes right padding from inline FLUID Text. NOTE:
   * this Prop is only applicable when "inline" Prop is true (default
   * FLUID Text has top/bottom margin instead of padding).
   */
  @Prop({ mutable: true }) disablePadding: boolean;

  /**
   * Removes top OR bottom margin when provided. To remove both, use
   * "disableGutters" Prop. Accepts "top" or "bottom".
   */
  @Prop({ mutable: true }) disableGutter: 'top' | 'bottom';

  /**
   * Optional color for the text, otherwise, inherits from parent.
   */
  @Prop({ mutable: true }) color: FluidColor;

  /**
   * Sets text alignment to left (default), right, or center.
   */
  @Prop({ mutable: true }) align: FluidAlignment;

  /**
   * When true, sequences of white space will be preserved (including
   * tabs), & lines will be broken at newline characters & <br> (i.e.
   * applies "white-space: pre-wrap" CSS rule). This overrides the
   * default behavior where newline characters, spaces, & tabs are
   * collapsed/removed.
   */
  @Prop({ mutable: true }) preserveSpacing: boolean;

  @Watch('preserveSpacing')
  onPreserveSpacingChange() {
    propSync(this, 'preserveSpacing', 'wrap', true);
  }

  /**
   * When true, font size will 12.5% smaller.
   */
  @Prop({ mutable: true }) small = false;

  /**
   * When true, character strings that are normally "unbreakable" (such
   * as long words or URLs) are allowed to be broken at arbitrary points if
   * there are no other acceptable break points in the line.
   */
  @Prop({ mutable: true }) breakWord = false;

  /**
   * Sets overflow behavior when text width is greater than the component's
   * width. By default, FLUID Text will wrap overflowing content onto new
   * line(s). Setting this Prop to "clip" will truncate text overflow
   * mid-character, and "ellipsis" will truncate text overflow with "..."
   * (NOTE: these values should only be used when seeing the entire text
   * is NOT necessary to understand the content's context/purpose). Both
   * "clip" & "ellipsis" values will display a tooltip on :hover containing
   * the full, unedited content.
   */
  @Prop({ mutable: true }) textOverflow: FluidTextOverflow =
    FluidTextOverflow.WRAP;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: deprecationWarning - outputs console deprecation warning for "wrap" Prop
   * @action: propSync - keeps deprecated "wrap" Prop in-sync with "preserveSpacing" Prop
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      init: () => setI18nState.bind(this)(),
      language: () => setI18nState.bind(this)(),
    });
    this.deprecationWarning();
    propSync(this, 'preserveSpacing', 'wrap', isNullOrUndefined(this.wrap));
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Outputs deprecation warning for "wrap" Prop (if used). Also outputs
   * clarification note describing the Prop's original purpose (
   */
  deprecationWarning() {
    if (isNullOrUndefined(this.wrap)) return;
    Utils.deprecationWarning(
      this.host,
      'Prop',
      'wrap',
      'preserveSpacing',
      'boolean',
      null,
      'NOTE: the "wrap" Prop is NOT needed to allow text wrapping - instead, it applies the CSS rule "white-space: pre-wrap" to the component. Text wrapping behavior is controlled by the "textOverflow" Prop.'
    );
  }

  /**
   * Returns correct semantic HTML tag for component's "root" element
   * based on Prop configuration.
   */
  getTextTag(): string {
    switch (true) {
      case this.strong:
        return 'strong';
      case this.inline || this.alignSuper:
        return 'span';
      default:
        return 'p';
    }
  }

  /**
   * Returns value for "title" attr IFF "textOverflow" Prop is either
   * "clip" or "wrap", otherwise returns undefined.
   */
  getTitleAttr(): string {
    if (
      isNullOrUndefined(this.textOverflow) ||
      this.textOverflow === FluidTextOverflow.WRAP
    )
      return;
    return this.host.textContent;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const TextTag = this.getTextTag();
    const titleAttr = this.getTitleAttr();
    const textClass = {
      [`fluid-text ${this.theme}`]: true,
      'center block': this.center,
      muted: this.muted,
      inline: this.inline,
      'super inline': this.alignSuper,
      block: this.block,
      italics: this.italics,
      'disable-gutters': this.disableGutters,
      'disable-top-gutter': this.disableGutter === 'top',
      'disable-bottom-gutter': this.disableGutter === 'bottom',
      'disable-padding': this.disablePadding,
      'pre-wrap': this.preserveSpacing,
      small: this.small,
      'break-word': this.breakWord,
      [`text-overflow-${this.textOverflow}`]: true,
      [`${this.align} block`]: !!this.align,
      [this.color]: !!this.color,
    };

    return (
      <TextTag class={textClass} {...(!!titleAttr && { title: titleAttr })}>
        <TranslatableSlot
          store={this._i18nStore}
          translationKey={this.fluidTranslate?.content}
        />
      </TextTag>
    );
  }

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "wrap" is now deprecated. Instead, use "preserveSpacing"
   * Prop (same functionality, just a different naming convention) --
   * When true, sequences of white space will be preserved (including
   * tabs), & lines will be broken at newline characters & <br> (i.e.
   * applies "white-space: pre-wrap" CSS rule). This overrides the
   * default behavior where newline characters, spaces, & tabs are
   * collapsed/removed.
   * @deprecated
   */
  @Prop({ mutable: true }) wrap: boolean;

  @Watch('wrap')
  onWrapChange() {
    propSync(this, 'preserveSpacing', 'wrap');
  }
}
